import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { SxProps, Theme } from '@mui/material/styles';
import { format } from 'date-fns';

function formatRangeDisplay(start: Date | null, end: Date | null) {
  if (!start || !end) return 'Select range';
  return `${format(start, 'dd/MM/yyyy HH:mm')} - ${format(end, 'dd/MM/yyyy HH:mm')}`;
}

interface TabPageHeaderProps {
  title: string;
  idPrefix: string;
  timeRangeStart: Date | null;
  timeRangeEnd: Date | null;
  onOpenRangePicker: () => void;
  refreshInterval: number;
  onRefreshIntervalChange: (val: number) => void;
  lastUpdated: Date | null;
  onRefresh: () => void;
  loading: boolean;
  styles: Record<string, SxProps<Theme>>;
}

export default function TabPageHeader({
  title,
  idPrefix,
  timeRangeStart,
  timeRangeEnd,
  onOpenRangePicker,
  refreshInterval,
  onRefreshIntervalChange,
  lastUpdated,
  onRefresh,
  loading,
  styles,
}: TabPageHeaderProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        variant="h4"
        id={`${idPrefix}-title`}
        sx={{ fontWeight: 'bold', color: 'text.primary' }}
      >
        {title} Service
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={onOpenRangePicker}
          sx={styles.timeRangeBtn}
          startIcon={<CalendarTodayIcon fontSize="small" />}
          id={`${idPrefix}-time-range-btn`}
          aria-label="Select time range"
        >
          {formatRangeDisplay(timeRangeStart, timeRangeEnd)}
        </Button>

        <FormControl variant="outlined" sx={styles.toolbarControl} size="small">
          <InputLabel id={`${idPrefix}-refresh-label`}>Auto-refresh</InputLabel>
          <Select
            labelId={`${idPrefix}-refresh-label`}
            id={`${idPrefix}-refresh-select`}
            label="Auto-refresh"
            value={refreshInterval}
            onChange={e => onRefreshIntervalChange(Number(e.target.value))}
          >
            <MenuItem value={0}>Off</MenuItem>
            <MenuItem value={30}>30s</MenuItem>
            <MenuItem value={60}>1m</MenuItem>
            <MenuItem value={300}>5m</MenuItem>
            <MenuItem value={600}>10m</MenuItem>
          </Select>
        </FormControl>

        {lastUpdated && (
          <Typography variant="body2" sx={{ ...styles.lastUpdated, fontSize: '0.9rem' }}>
            <AccessTimeIcon fontSize="small" sx={{ opacity: 0.8 }} />
            {format(lastUpdated, 'HH:mm:ss')}
          </Typography>
        )}

        <Tooltip title="Refresh now">
          <IconButton
            onClick={onRefresh}
            disabled={loading}
            size="small"
            id={`${idPrefix}-refresh-btn`}
            aria-label="Refresh data"
            sx={{
              color: 'var(--color-primary)',
              '&:hover': { backgroundColor: 'rgba(94, 51, 191, 0.08)' },
              '&.Mui-disabled': {
                color: 'rgba(94, 51, 191, 0.3)',
              },
            }}
          >
            <RefreshIcon className={loading ? 'rotate' : ''} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
